import { useCallback, useContext, useEffect } from "react";
import { create, StoreApi, UseBoundStore } from "zustand";

import { BuilderActionInput } from "@src/types.generated";
import { secondsBetweenDates, secondsSinceDate } from "@src/lib/utils/dates";
import { uuid4 } from "@src/lib/utils/strings";
import { ClientIdContext } from "@src/features/builders/shared/presence/ClientIdContext";

// ─── Constants ──────────────────────────────────────────────────────────

// How long after the first error we keep retrying at the fast cadence.
export const GIVE_UP_AFTER_SECONDS = 50;

// After the fast-retry window, keep retrying at this slower cadence
// instead of stopping: a queue that stops flushing while holding edits
// is silent data loss on the next reload (OC-779).
export const SLOW_RETRY_INTERVAL_SECONDS = 30;

// How long an in-flight mutation may gate the flush loop. Past this,
// re-dispatch even though `loading` is still true: the server applies
// actions by uuid idempotently so a duplicate send is a no-op, while a
// never-settling request would otherwise freeze the queue forever.
export const STUCK_INFLIGHT_SECONDS = 60;

// Minimum wait between flushes — gives a rapid burst of actions a
// chance to collect into one batch.
export const DEBOUNCE_SECONDS = 1;

// Max actions sent per flush. Larger batches mean fewer round-trips
// but bigger payloads; 10 is the established LibraryItem default.
export const MAX_PER_FLUSH = 10;

// ─── Pure helpers ───────────────────────────────────────────────────────

/**
 * Append `next` to the queue. If `shouldDedupe(existing, next)`
 * returns true for any existing item, that item is dropped — the
 * latest enqueue wins. Used to collapse rapid same-target updates
 * (e.g. name edits or, for the workflow canvas, drag-stops on the
 * same node).
 */
export const enqueueWithDedupe = <T>(
  queue: ReadonlyArray<T>,
  next: T,
  shouldDedupe?: (existing: T, next: T) => boolean,
): T[] => {
  if (!shouldDedupe) return [...queue, next];
  return [...queue.filter((existing) => !shouldDedupe(existing, next)), next];
};

/**
 * Drop items whose uuid appears in `appliedUuids`. Used after a
 * successful flush to evict the items the server confirmed it
 * applied, leaving the rest in the queue for the next flush.
 */
export const evictApplied = <T extends { uuid: string }>(
  queue: ReadonlyArray<T>,
  appliedUuids: ReadonlySet<string>,
): T[] => queue.filter((item) => !appliedUuids.has(item.uuid));

/**
 * True if we should attempt a flush despite a prior error. For the
 * first 50 seconds after the first error we retry at the driver's
 * fast cadence; after that we degrade to one attempt every
 * SLOW_RETRY_INTERVAL_SECONDS (measured against lastSyncAt, which
 * recordError/markFlushStart keep current) — never stopping outright,
 * so a transient failure can't strand queued edits until reload.
 * Resets when a flush succeeds (caller sets firstSyncErrorAt back
 * to null).
 */
export const shouldRetryFailedSync = (
  firstSyncErrorAt: Date | null | undefined,
  lastSyncAt: Date,
  now: Date = new Date(),
): boolean => {
  if (!firstSyncErrorAt) return true;
  if (secondsBetweenDates(firstSyncErrorAt, now) < GIVE_UP_AFTER_SECONDS)
    return true;
  return secondsBetweenDates(lastSyncAt, now) >= SLOW_RETRY_INTERVAL_SECONDS;
};

// ─── Apollo handler factory ─────────────────────────────────────────────

/**
 * Structural shape of every `applyBuilderActions` mutation response.
 * Generated mutations differ in name but the field shape is the same.
 */
type ApplyBuilderActionsResponse = {
  applyBuilderActions: {
    success: boolean;
    appliedBuilderActions?: Array<{ uuid: string }> | null;
  };
};

/**
 * Returns Apollo `onCompleted` / `onError` handlers that route success
 * / failure into the store's `recordSuccess` / `recordError` actions.
 * Spread the result into a generated `useApplyXMutation` options
 * object alongside any consumer-specific options (e.g. refetchQueries).
 */
export const createSyncMutationHandlers = ({
  recordSuccess,
  recordError,
}: {
  recordSuccess: (appliedUuids: ReadonlySet<string>) => void;
  recordError: () => void;
}): {
  onCompleted: (data: ApplyBuilderActionsResponse | null | undefined) => void;
  onError: () => void;
} => ({
  onCompleted: (data) => {
    const r = data?.applyBuilderActions;
    if (!r) return;
    if (r.success) {
      const applied = r.appliedBuilderActions ?? [];
      recordSuccess(new Set(applied.map((a) => a.uuid)));
    } else {
      recordError();
    }
  },
  onError: () => recordError(),
});

/**
 * Hook wrapper around `createSyncMutationHandlers` — reads the store's
 * recordSuccess / recordError actions and returns Apollo handlers
 * ready to spread into a `useApplyXMutation` options object.
 */
export const useSyncMutationHandlers = <TItem extends { uuid: string }>(
  store: UseBoundStore<StoreApi<BuilderActionsSyncStore<TItem>>>,
): ReturnType<typeof createSyncMutationHandlers> => {
  const recordSuccess = store((s) => s.recordSuccess);
  const recordError = store((s) => s.recordError);
  return createSyncMutationHandlers({ recordSuccess, recordError });
};

// ─── Store factory ──────────────────────────────────────────────────────

/**
 * Shared shape for any BuilderActions sync queue (workflow,
 * LibraryItem, future). The store owns the queue + sync timestamps
 * and exposes the standard mutations. Per-domain divergence (queue
 * item shape, dedupe predicate, mutation hook, transform) stays in
 * each consumer's hook layer.
 */
export type BuilderActionsSyncStore<TItem extends { uuid: string }> = {
  queue: TItem[];
  lastSyncAt: Date;
  firstSyncErrorAt: Date | null;
  lastSuccessfulSyncAt: Date | null;
  enqueue: (item: TItem) => void;
  markFlushStart: () => void;
  recordSuccess: (appliedUuids: ReadonlySet<string>) => void;
  recordError: () => void;
  resetErrorBudget: () => void;
};

export const createBuilderActionsSyncStore = <TItem extends { uuid: string }>(
  shouldDedupe: (existing: TItem, next: TItem) => boolean,
): UseBoundStore<StoreApi<BuilderActionsSyncStore<TItem>>> =>
  create<BuilderActionsSyncStore<TItem>>((set) => ({
    queue: [],
    lastSyncAt: new Date(0),
    firstSyncErrorAt: null,
    lastSuccessfulSyncAt: null,
    enqueue: (item) =>
      set((s) => ({
        queue: enqueueWithDedupe(s.queue, item, shouldDedupe),
      })),
    markFlushStart: () => set({ lastSyncAt: new Date() }),
    recordSuccess: (appliedUuids) =>
      set((s) => ({
        queue:
          appliedUuids.size === 0
            ? s.queue
            : evictApplied(s.queue, appliedUuids),
        lastSuccessfulSyncAt: new Date(),
        firstSyncErrorAt: null,
      })),
    recordError: () =>
      set((s) => ({
        lastSyncAt: new Date(),
        firstSyncErrorAt: s.firstSyncErrorAt ?? new Date(),
      })),
    resetErrorBudget: () => set({ firstSyncErrorAt: null }),
  }));

// ─── Enqueue wrapper hook ───────────────────────────────────────────────

/**
 * Builds the standard "wrap input with uuid + clientId, push to store"
 * hook. LibraryItem's `BuilderActionPartial` slots directly into
 * `Omit<TItem, "uuid" | "clientId">`. Workflow's input is just
 * `payload`, so its consumer keeps a one-line wrapper to nest it.
 */
export const useEnqueueBuilderAction = <
  TItem extends { uuid: string; clientId: string },
>(
  store: UseBoundStore<StoreApi<BuilderActionsSyncStore<TItem>>>,
): ((item: Omit<TItem, "uuid" | "clientId">) => void) => {
  const clientIdContext = useContext(ClientIdContext);
  const enqueueOnStore = store((s) => s.enqueue);
  return useCallback(
    (item) => {
      if (!clientIdContext) return;
      enqueueOnStore({
        uuid: uuid4(),
        clientId: clientIdContext.clientId,
        ...item,
      } as TItem);
    },
    [clientIdContext, enqueueOnStore],
  );
};

// ─── Flush driver ───────────────────────────────────────────────────────

type FlushDriverArgs<TItem extends { uuid: string }> = {
  store: UseBoundStore<StoreApi<BuilderActionsSyncStore<TItem>>>;
  // Apollo's mutate signature, so consumers can pass the generated
  // hook's mutate function directly without wrapping.
  mutate: (opts: { variables: { actions: BuilderActionInput[] } }) => unknown;
  loading: boolean;
  transform: (item: TItem) => BuilderActionInput;
};

/**
 * Owns the flush lifecycle for a BuilderActions sync queue:
 * - Reactive flush whenever the queue or `loading` flips.
 * - Periodic retry every second (post-error retry path).
 * - Resets `firstSyncErrorAt` on mount so a poisoned budget from a
 *   prior session doesn't gate every flush in this one.
 * - Best-effort final flush on unmount. Apollo keeps the mutation
 *   in-flight after unmount; `onCompleted` won't fire so the queue
 *   isn't evicted, but the server's uuid idempotency makes a
 *   duplicate send on next mount a no-op.
 *
 * Mount at a level that outlives every component that can enqueue.
 */
export const useBuilderActionsFlushDriver = <TItem extends { uuid: string }>({
  store,
  mutate,
  loading,
  transform,
}: FlushDriverArgs<TItem>): void => {
  const markFlushStart = store((s) => s.markFlushStart);
  const resetErrorBudget = store((s) => s.resetErrorBudget);

  const flush = useCallback(() => {
    const { queue, lastSyncAt, firstSyncErrorAt } = store.getState();
    if (queue.length === 0) return;
    if (loading && secondsSinceDate(lastSyncAt) < STUCK_INFLIGHT_SECONDS)
      return;
    if (secondsSinceDate(lastSyncAt) <= DEBOUNCE_SECONDS) return;
    if (!shouldRetryFailedSync(firstSyncErrorAt, lastSyncAt)) return;
    markFlushStart();
    mutate({
      variables: { actions: queue.slice(0, MAX_PER_FLUSH).map(transform) },
    });
  }, [store, mutate, loading, markFlushStart, transform]);

  useEffect(() => {
    resetErrorBudget();
  }, [resetErrorBudget]);

  // Fire immediately when `flush` identity changes (loading flipped,
  // queue grew, etc.) and keep retrying once per second to cover the
  // post-error case where the reactive trigger doesn't fire again.
  useEffect(() => {
    flush();
    const interval = setInterval(flush, 1000);
    return () => clearInterval(interval);
  }, [flush]);

  useEffect(() => {
    return () => {
      const { queue } = store.getState();
      if (queue.length === 0) return;
      mutate({
        variables: { actions: queue.slice(0, MAX_PER_FLUSH).map(transform) },
      });
    };
  }, [store, mutate, transform]);

  // The queue lives only in memory: navigating away or reloading with
  // queued edits discards them, so make the browser ask first.
  useEffect(() => {
    const warnOnUnloadWithUnsaved = (e: BeforeUnloadEvent) => {
      if (store.getState().queue.length === 0) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warnOnUnloadWithUnsaved);
    return () =>
      window.removeEventListener("beforeunload", warnOnUnloadWithUnsaved);
  }, [store]);
};
